import Hero from "@/components/site/Hero";
import Services from "@/components/site/Services";
import Formules from "@/components/site/Formules";
import Galerie from "@/components/site/Galerie";
import ZonesInterventions from "@/components/site/ZonesInterventions";
import PrixDemenagement from "@/components/site/PrixDemenagement";
import PourquoiChoisir from "@/components/site/PourquoiChoisir";
import AssuranceSection from "@/components/site/AssuranceSection";
import NotreAdresse from "@/components/site/NotreAdresse";
import Avis from "@/components/site/Avis";
import useSEO from "@/hooks/useSEO";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, CheckCircle2 } from "lucide-react";
import { useState, useRef } from "react";

const SITE_URL = "https://bigdemenagement.fr";

// Sitelinks order = menu order + "Devis gratuit" en dernier
const SITELINKS = [
  { name: "Accueil",     url: `${SITE_URL}/` },
  { name: "Particulier", url: `${SITE_URL}/demenagement-particulier` },
  { name: "Entreprise",  url: `${SITE_URL}/entreprise` },
  { name: "Garde-meuble", url: `${SITE_URL}/garde-meuble` },
  { name: "Monte-meuble", url: `${SITE_URL}/monte-meuble` },
  { name: "Formules",    url: `${SITE_URL}/formules` },
  { name: "Calculateur", url: `${SITE_URL}/calculer-mon-volume` },
  { name: "FAQ", url: `${SITE_URL}/faq` },
  { name: "Devis gratuit", url: `${SITE_URL}/demande-de-devis` },
];

const HOME_JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "Big Déménagement",
      alternateName: "BIG DEMENAGEMENT",
      url: `${SITE_URL}/`,
      logo: `${SITE_URL}/images/logo-blanc.webp`,
      image: `${SITE_URL}/images/logo-blanc.webp`,
      email: "contact@bigdem.fr",
      telephone: "+33185117279",
      address: {
        "@type": "PostalAddress",
        streetAddress: "17 Rue Henry Monnier",
        addressLocality: "Paris",
        postalCode: "75009",
        addressCountry: "FR",
      },
      areaServed: { "@type": "Country", name: "France" },
      sameAs: [],
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: `${SITE_URL}/`,
      name: "Big Déménagement",
      inLanguage: "fr-FR",
      publisher: { "@id": `${SITE_URL}/#organization` },
    },
    {
      "@type": "WebPage",
      "@id": `${SITE_URL}/#webpage`,
      url: `${SITE_URL}/`,
      name: "Big Déménagement | Déménageur Île-de-France",
      isPartOf: { "@id": `${SITE_URL}/#website` },
      inLanguage: "fr-FR",
      description:
        "Société de déménagement en Île-de-France. Particuliers, entreprises, international, monte-meuble, garde-meuble. Devis gratuit.",
    },
    {
      "@type": "ItemList",
      "@id": `${SITE_URL}/#sitelinks`,
      name: "Pages principales",
      itemListElement: SITELINKS.map((s, i) => ({
        "@type": "SiteNavigationElement",
        position: i + 1,
        name: s.name,
        url: s.url,
      })),
    },
    {
      "@type": "FAQPage",
      "@id": `${SITE_URL}/#faq`,
      mainEntity: [
        {
          "@type": "Question",
          name: "Comment obtenir un devis de déménagement en ligne ? Votre estimation gratuite",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Remplissez notre formulaire de devis en ligne ou contactez-nous directement par téléphone au 01 85 11 72 79. Un conseiller dédié analyse votre projet (adresses de départ et d'arrivée, date, type de logement, volume, accès, besoins spécifiques), une visite technique gratuite peut être organisée sur place ou en visioconférence, puis vous recevez rapidement un devis clair, détaillé et sans engagement.",
          },
        },
        {
          "@type": "Question",
          name: "Quelles sont les contraintes d'un déménagement en Île-de-France ?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Les contraintes varient selon le secteur : stationnement du camion et autorisation de voirie dans les zones urbaines denses, étages sans ascenseur ou cages d'escalier étroites pouvant nécessiter un monte-meubles, circulation plus dense à Paris et en petite couronne, distances plus longues en grande couronne, et distance de portage selon les accès (rue piétonne, cour intérieure, résidence réglementée, parking souterrain). Chaque département — Hauts-de-Seine, Val-de-Marne, Seine-Saint-Denis, Yvelines, Essonne, Seine-et-Marne, Val-d'Oise — présente ses propres spécificités : l'étude des accès et des conditions d'intervention fait partie de la préparation de chaque déménagement. En anticipant ces éléments avant le jour J, il est possible d'adapter le camion, le nombre de déménageurs, le matériel et le planning.",
          },
        },
        {
          "@type": "Question",
          name: "Faut-il une autorisation de stationnement pour déménager ?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Oui, dans la plupart des communes une autorisation d'occupation temporaire (AOT) de la voirie est nécessaire pour stationner le camion de déménagement. Big Déménagement prend en charge la demande d'arrêté auprès de la mairie pour garantir un emplacement au pied de votre immeuble.",
          },
        },
        {
          "@type": "Question",
          name: "Dans quels cas faut-il utiliser un monte-meuble ?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Le monte-meuble est recommandé lorsque les escaliers sont étroits, que l'ascenseur est inadapté ou que les meubles sont volumineux. Il permet de passer par les fenêtres ou les balcons pour sécuriser la manutention, protéger les parties communes et gagner un temps précieux lors du chargement.",
          },
        },
        {
          "@type": "Question",
          name: "Quelle formule de déménagement choisir ?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Big Déménagement propose quatre formules modulables, de l'offre économique à la prestation clé en main. Le choix dépend de votre budget, de votre volume et du niveau d'accompagnement souhaité (emballage, démontage, déballage). Notre équipe vous conseille la formule la plus adaptée lors de l'établissement du devis.",
          },
        },
        {
          "@type": "Question",
          name: "Démontez-vous et remontez-vous les meubles ?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Oui, nos déménageurs prennent en charge le démontage et le remontage de vos meubles (armoires, lits, bibliothèques, tables) selon la formule choisie. Chaque élément est protégé pendant le transport puis réassemblé dans votre nouveau logement, ce qui limite les risques de casse et facilite le passage dans les accès étroits.",
          },
        },
        {
          "@type": "Question",
          name: "Combien de cartons prévoir pour un déménagement ?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Le nombre de cartons dépend de la superficie habitable, du nombre de pièces et du cubage total de vos biens. À titre indicatif : studio/T1 environ 10 à 15 cartons, appartement T2 environ 20 à 30 cartons, T3 environ 30 à 50 cartons, maison ou grand appartement (T4 et plus) environ 50 à 80 cartons voire davantage selon les annexes. Privilégiez les petits cartons renforcés pour les livres et la vaisselle, réservez les grands formats aux textiles, protégez les objets délicats au papier bulle et prévoyez une marge de 10 % à 15 % de fournitures supplémentaires. Notre équipe vous guide dans le choix des kits et met à disposition tout le matériel professionnel nécessaire.",
          },
        },
        {
          "@type": "Question",
          name: "Comment réduire le prix de son déménagement ?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Plusieurs leviers permettent de réduire le coût : trier vos affaires en amont pour réduire le cubage, préparer vous-même vos cartons avec une formule plus économique, sélectionner uniquement les prestations nécessaires, choisir une date flexible hors périodes de forte tension (fins de mois, week-ends estivaux), anticiper les autorisations de stationnement et exiger un devis détaillé et transparent. Le prix dépend du cubage, de la distance, de l'accessibilité des logements, de la saisonnalité et des services choisis.",
          },
        },
        {
          "@type": "Question",
          name: "Que comprend une prestation de déménagement professionnel ?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Une prestation professionnelle comprend la protection et le conditionnement du mobilier, la mise en carton des objets fragiles, la manutention et le transport en camion capitonné, le démontage et remontage du mobilier selon la formule, l'emballage global pour les formules clés en main, l'utilisation de matériel technique professionnel et la livraison avec installation dans chaque pièce. Des services complémentaires sont disponibles à la carte : livraison de cartons à domicile, monte-meuble, stockage en garde-meuble, service débarras et réservation de stationnement.",
          },
        },
        {
          "@type": "Question",
          name: "Comment préparer efficacement son déménagement étape par étape ?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "À J-30 : triez vos affaires, commandez vos cartons, signalez votre changement d'adresse et demandez l'autorisation de stationnement. À J-15 : emballez les objets non essentiels, étiquetez chaque carton et regroupez vos documents importants. À J-7 : démontez le mobilier non indispensable, dégivrez le réfrigérateur et préparez une valise de première urgence. À J-2 : finalisez les derniers cartons et confirmez les détails logistiques. Le jour J : validez l'état des lieux, assistez au chargement puis guidez les déménageurs à l'arrivée avant de signer la lettre de voiture. Après : déballez par ordre de priorité et actualisez vos documents administratifs.",
          },
        },
      ],
    },
  ],
};

const HOME_FAQ_VISIBLE = [
  {
    q: "Comment obtenir un devis de déménagement en ligne ? Votre estimation gratuite",
    a: (
      <>
        <p>
          Vous planifiez un changement de domicile ou le transfert de vos bureaux ? Pour connaître
          précisément le tarif d&apos;un déménageur professionnel, la première étape essentielle
          est de demander une estimation personnalisée. Chez{" "}
          <strong className="text-[#0f1d2b] font-semibold">Big Déménagement</strong>, nous vous
          proposons une démarche fluide, transparente et{" "}
          <strong className="text-[#0f1d2b] font-semibold">sans aucun engagement</strong> pour
          évaluer le coût de votre projet.
        </p>

        <ol className="mt-6 space-y-5 pl-1 list-none">
          <li>
            <p className="font-semibold text-[#0f1d2b]">1. Contactez-nous</p>
            <p className="mt-2">
              Pour démarrer votre projet, il vous suffit de remplir notre{" "}
              <Link
                to="/demande-de-devis"
                className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
              >
                formulaire de demande de devis en ligne
              </Link>{" "}
              ou de nous contacter directement par téléphone.
            </p>
            <p className="mt-2">
              Un conseiller dédié prendra le temps d&apos;écouter votre projet de déménagement, de
              comprendre vos besoins et de répondre à vos premières questions.
            </p>
          </li>

          <li>
            <p className="font-semibold text-[#0f1d2b]">2. Analyse de votre projet</p>
            <p className="mt-2">
              Lors de cet échange, nous recueillons les informations essentielles concernant votre
              déménagement :
            </p>
            <ul className="mt-2 space-y-1.5 pl-1">
              {[
                "Adresse de départ et d'arrivée",
                "Date souhaitée",
                "Type de logement",
                "Volume estimé à déménager",
                "Accès aux logements",
                "Besoins spécifiques (monte-meubles, garde-meubles, objets fragiles, etc.)",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[#237cb5]" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2">
              Selon la taille et la complexité de votre projet, votre conseiller vous proposera la
              solution la plus adaptée.
            </p>
          </li>

          <li>
            <p className="font-semibold text-[#0f1d2b]">3. Visite technique ou étude à distance</p>
            <p className="mt-2">
              Afin d&apos;établir un devis précis, nous pouvons réaliser :
            </p>
            <div className="mt-3 space-y-3">
              <div>
                <p className="font-semibold text-[#0f1d2b]">Une visite à domicile</p>
                <p className="mt-1">
                  Un expert se déplace chez vous pour évaluer précisément le volume à déménager et
                  les éventuelles contraintes techniques.
                </p>
              </div>
              <div>
                <p className="font-semibold text-[#0f1d2b]">Une visite virtuelle</p>
                <p className="mt-1">
                  Simple et rapide, elle permet d&apos;effectuer une estimation à distance par
                  téléphone ou visioconférence.
                </p>
              </div>
              <div>
                <p className="font-semibold text-[#0f1d2b]">Une étude sur inventaire</p>
                <p className="mt-1">
                  Pour les projets les plus simples, vous pouvez nous transmettre directement la
                  liste de vos biens ainsi que les informations relatives à votre déménagement.
                </p>
              </div>
            </div>
          </li>

          <li>
            <p className="font-semibold text-[#0f1d2b]">
              4. Étude personnalisée de votre déménagement
            </p>
            <p className="mt-2">
              Nos équipes analysent l&apos;ensemble des informations recueillies afin de vous
              proposer la formule la plus adaptée à votre situation.
            </p>
            <p className="mt-2">Nous prenons en compte :</p>
            <ul className="mt-2 space-y-1.5 pl-1">
              {[
                "Le volume à transporter",
                "La distance à parcourir",
                "Les conditions d'accès",
                "Les services souhaités",
                "Les contraintes particulières de votre déménagement",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[#237cb5]" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2">
              Notre objectif est de vous proposer la solution la plus efficace et la plus
              avantageuse.
            </p>
          </li>

          <li>
            <p className="font-semibold text-[#0f1d2b]">5. Réception de votre devis détaillé</p>
            <p className="mt-2">
              À l&apos;issue de l&apos;étude de votre projet, nous vous transmettons un devis
              clair, détaillé et sans surprise.
            </p>
            <p className="mt-2">Vous y retrouverez :</p>
            <ul className="mt-2 space-y-1.5 pl-1">
              {[
                "Les prestations incluses",
                "Les options choisies",
                "Les conditions d'intervention",
                "Le tarif correspondant à votre projet",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[#237cb5]" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2">
              Notre équipe reste à votre disposition pour vous expliquer chaque élément du devis et
              répondre à toutes vos interrogations.
            </p>
          </li>

          <li>
            <p className="font-semibold text-[#0f1d2b]">6. Accompagnement avant le déménagement</p>
            <p className="mt-2">
              Une fois votre devis validé, vous bénéficiez d&apos;un suivi personnalisé
              jusqu&apos;au jour du déménagement.
            </p>
            <p className="mt-2">Votre conseiller reste disponible pour :</p>
            <ul className="mt-2 space-y-1.5 pl-1">
              {[
                "Répondre à vos questions",
                "Vous accompagner dans l'organisation",
                "Vous fournir des conseils pratiques",
                "Préparer au mieux votre déménagement",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[#237cb5]" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2">
              Nous mettons tout en œuvre pour que votre projet se déroule dans les meilleures
              conditions.
            </p>
          </li>

          <li>
            <p className="font-semibold text-[#0f1d2b]">7. Réalisation de votre déménagement</p>
            <p className="mt-2">
              Le jour J, nos déménageurs professionnels prennent en charge votre mobilier et vos
              effets personnels avec le plus grand soin.
            </p>
            <p className="mt-2">Nos équipes assurent :</p>
            <ul className="mt-2 space-y-1.5 pl-1">
              {[
                "La protection de vos biens",
                "Le chargement sécurisé",
                "Le transport",
                "La livraison à destination",
                "L'installation selon la formule choisie",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[#237cb5]" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2">
              Vous profitez ainsi d&apos;un déménagement serein, réalisé par des professionnels
              expérimentés.
            </p>
          </li>
        </ol>

        <div className="mt-6">
          <Link
            to="/demande-de-devis"
            data-testid="home-faq-devis-cta"
            className="h-11 inline-flex items-center justify-center gap-2 rounded-xl px-5 bg-[#FF7A00] hover:bg-[#E66E00] text-white text-[14px] font-semibold shadow-[0_18px_30px_-12px_rgba(255,122,0,0.55)] transition-colors whitespace-nowrap"
          >
            Devis rapide
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </>
    ),
  },
  {
    q: "Quelles sont les contraintes d'un déménagement en Île-de-France ?",
    a: (
      <>
        <p>
          Déménager en Île-de-France présente des contraintes très différentes selon que
          l&apos;on quitte un appartement parisien, une résidence de petite couronne ou une
          maison située en grande couronne. Stationnement, circulation, étages, accès au
          logement et distance de portage doivent être anticipés pour éviter les difficultés le
          jour du déménagement.
        </p>
        <p className="mt-5 font-extrabold text-[#0f1d2b]">
          Stationnement et autorisation de voirie
        </p>
        <p className="mt-2">
          Dans les secteurs urbains denses, stationner un camion de déménagement à proximité du
          logement peut être compliqué. Selon la commune et l&apos;emplacement nécessaire, une{" "}
          <Link
            to="/autorisation-de-stationnement-demenagement"
            data-testid="home-faq-idf-aot-link"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            autorisation de stationnement
          </Link>{" "}
          ou d&apos;occupation temporaire de la voie publique peut être nécessaire. Anticiper
          cette démarche permet de réserver un emplacement adapté et de limiter la distance
          entre le camion et le logement.
        </p>
        <p className="mt-5 font-extrabold text-[#0f1d2b]">
          Étages, ascenseurs et monte-meubles
        </p>
        <p className="mt-2">
          Un appartement situé en étage sans ascenseur, un ascenseur trop petit ou une cage
          d&apos;escalier étroite peuvent rendre la manutention plus difficile. Lorsque
          certains meubles ne peuvent pas passer par les parties communes, l&apos;utilisation
          d&apos;un{" "}
          <Link
            to="/monte-meuble"
            data-testid="home-faq-idf-mm-link"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            monte-meubles
          </Link>{" "}
          peut constituer une solution plus rapide et plus sécurisée.
        </p>
        <p className="mt-5 font-extrabold text-[#0f1d2b]">
          Circulation en petite et grande couronne
        </p>
        <p className="mt-2">
          Les conditions de circulation varient fortement selon le secteur et l&apos;horaire.
          Paris et la petite couronne présentent généralement une circulation plus dense,
          tandis qu&apos;un déménagement en grande couronne peut impliquer davantage de
          kilomètres entre les différentes adresses. L&apos;horaire de départ et
          l&apos;organisation du trajet doivent donc être intégrés à la préparation.
        </p>
        <p className="mt-5 font-extrabold text-[#0f1d2b]">
          Accès au logement et distance de portage
        </p>
        <p className="mt-2">
          La distance entre le camion et l&apos;entrée du logement joue également un rôle
          important. Rue piétonne, cour intérieure, résidence avec accès réglementé, portail,
          parking souterrain ou impossibilité d&apos;approcher le camion : ces éléments peuvent
          augmenter la distance de portage et nécessiter une organisation particulière.
        </p>
        <p className="mt-5 font-extrabold text-[#0f1d2b]">
          Des contraintes différentes selon les départements
        </p>
        <p className="mt-2">
          Un déménagement dans les{" "}
          <Link
            to="/demenagement-hauts-de-seine-92"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            Hauts-de-Seine
          </Link>
          , le{" "}
          <Link
            to="/demenagement-val-de-marne-94"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            Val-de-Marne
          </Link>{" "}
          ou la{" "}
          <Link
            to="/demenagement-seine-saint-denis-93"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            Seine-Saint-Denis
          </Link>{" "}
          ne présente pas forcément les mêmes contraintes qu&apos;un déménagement dans les{" "}
          <Link
            to="/demenagement-yvelines-78"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            Yvelines
          </Link>
          , l&apos;
          <Link
            to="/demenagement-essonne-91"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            Essonne
          </Link>
          , la{" "}
          <Link
            to="/demenagement-seine-et-marne-77"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            Seine-et-Marne
          </Link>{" "}
          ou le{" "}
          <Link
            to="/demenagement-val-d-oise-95"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            Val-d&apos;Oise
          </Link>
          . C&apos;est pourquoi l&apos;étude des accès et des conditions d&apos;intervention
          fait partie de la préparation de chaque déménagement.
        </p>
        <p className="mt-4">
          En anticipant ces différents éléments avant le jour J, il est possible d&apos;adapter
          le camion, le nombre de déménageurs, le matériel et le planning afin d&apos;organiser
          le déménagement dans de bonnes conditions.
        </p>
      </>
    ),
  },
  {
    q: "Faut-il une autorisation de stationnement pour déménager ?",
    a: (
      <>
        <p>
          Selon l&apos;adresse et les conditions d&apos;accès, une{" "}
          <strong className="text-[#0f1d2b] font-semibold">
            autorisation de stationnement
          </strong>{" "}
          peut être nécessaire pour réserver un emplacement au camion de déménagement. Elle est
          généralement indispensable lorsqu&apos;un{" "}
          <Link
            to="/monte-meuble"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            monte-meuble
          </Link>{" "}
          doit être utilisé,
          notamment pour garantir la sécurité de l&apos;intervention et éviter de bloquer la
          circulation.
        </p>
        <p className="mt-3">
          Une demande peut également être nécessaire dans les{" "}
          <strong className="text-[#0f1d2b] font-semibold">rues étroites</strong>, les{" "}
          <strong className="text-[#0f1d2b] font-semibold">zones très fréquentées</strong> ou
          lorsque le stationnement est difficile. La procédure doit souvent être effectuée{" "}
          <strong className="text-[#0f1d2b] font-semibold">plusieurs jours à l&apos;avance</strong>{" "}
          auprès de la mairie ou des services concernés.
        </p>
        <p className="mt-3">
          <strong className="text-[#0f1d2b] font-semibold">Big Déménagement</strong> peut prendre
          en charge les{" "}
          <strong className="text-[#0f1d2b] font-semibold">démarches administratives</strong> pour
          vous afin de faciliter l&apos;organisation de votre déménagement.
        </p>
        <p className="mt-3">
          Pour connaître la procédure, les délais et les tarifs, consultez notre{" "}
          <Link
            to="/autorisation-de-stationnement-demenagement"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            page dédiée à l&apos;autorisation de stationnement
          </Link>
          .
        </p>

        <div className="mt-6">
          <Link
            to="/autorisation-de-stationnement-demenagement"
            data-testid="home-faq-aot-cta"
            className="h-11 inline-flex items-center justify-center gap-2 rounded-xl px-5 bg-[#0f1d2b] hover:bg-[#1c2d40] text-white text-[14px] font-semibold transition-colors whitespace-nowrap"
          >
            Voir notre page autorisation de stationnement
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </>
    ),
  },
  {
    q: "Dans quels cas faut-il utiliser un monte-meuble ?",
    a: (
      <>
        <p>
          L&apos;utilisation d&apos;un{" "}
          <strong className="text-[#0f1d2b] font-semibold">monte-meuble</strong> est recommandée
          lorsque certains meubles ou objets volumineux ne peuvent pas passer par les escaliers,
          l&apos;ascenseur ou les parties communes de l&apos;immeuble. Cela concerne notamment les{" "}
          <strong className="text-[#0f1d2b] font-semibold">
            canapés, armoires, pianos, appareils électroménagers
          </strong>{" "}
          ou meubles particulièrement lourds.
        </p>
        <p className="mt-3">
          Le monte-meuble est également très utile pour les logements situés à un{" "}
          <strong className="text-[#0f1d2b] font-semibold">étage élevé</strong>, dans les{" "}
          <strong className="text-[#0f1d2b] font-semibold">immeubles sans ascenseur</strong> ou
          lorsque les accès sont étroits et compliqués. Il permet de{" "}
          <strong className="text-[#0f1d2b] font-semibold">gagner du temps</strong>, de{" "}
          <strong className="text-[#0f1d2b] font-semibold">limiter les risques de casse</strong> et
          d&apos;éviter d&apos;endommager les murs, les escaliers ou les meubles.
        </p>
        <p className="mt-3">
          <strong className="text-[#0f1d2b] font-semibold">Big Déménagement</strong> dispose de son
          propre monte-meuble et peut l&apos;intégrer directement à votre prestation de
          déménagement. Selon l&apos;adresse, une{" "}
          <Link
            to="/autorisation-de-stationnement-demenagement"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            autorisation de stationnement
          </Link>{" "}
          peut être nécessaire pour permettre son installation dans de bonnes conditions.
        </p>

        <div className="mt-6 flex flex-col sm:flex-row gap-2.5">
          <Link
            to="/monte-meuble"
            data-testid="home-faq-monte-meuble-cta"
            className="h-11 inline-flex items-center justify-center gap-2 rounded-xl px-5 bg-[#FF7A00] hover:bg-[#E66E00] text-white text-[14px] font-semibold shadow-[0_18px_30px_-12px_rgba(255,122,0,0.55)] transition-colors whitespace-nowrap"
          >
            Découvrir notre service monte-meuble
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/devis-monte-meuble"
            data-testid="home-faq-monte-meuble-devis-cta"
            className="h-11 inline-flex items-center justify-center gap-2 rounded-xl px-5 bg-white border border-[#e6edf3] hover:border-[#FF7A00] text-[#0f1d2b] hover:text-[#FF7A00] text-[14px] font-semibold transition-colors whitespace-nowrap"
          >
            Devis monte-meuble
          </Link>
        </div>
      </>
    ),
  },
  {
    q: "Quelle formule de déménagement choisir ?",
    a: (
      <>
        <p>
          Le choix de votre formule dépend principalement de votre{" "}
          <strong className="text-[#0f1d2b] font-semibold">budget</strong>, du{" "}
          <strong className="text-[#0f1d2b] font-semibold">temps</strong> dont vous disposez et des{" "}
          <strong className="text-[#0f1d2b] font-semibold">tâches</strong> que vous souhaitez
          confier à nos déménageurs.
        </p>
        <p className="mt-3">
          La formule{" "}
          <strong className="text-[#0f1d2b] font-semibold">Économique</strong> convient aux
          personnes qui souhaitent préparer elles-mêmes leur déménagement, notamment l&apos;emballage
          et le déballage des cartons, tout en bénéficiant de la protection du mobilier, du
          chargement, du transport et de la livraison.
        </p>
        <p className="mt-3">
          La formule{" "}
          <strong className="text-[#0f1d2b] font-semibold">Économique Plus</strong> offre une prise
          en charge plus complète, avec des services supplémentaires pour faciliter la préparation
          de votre déménagement.
        </p>
        <p className="mt-3">
          La formule{" "}
          <strong className="text-[#0f1d2b] font-semibold">Standard</strong> est idéale pour gagner
          du temps et bénéficier d&apos;un accompagnement renforcé, notamment pour l&apos;emballage
          des objets fragiles et certaines opérations liées au mobilier.
        </p>
        <p className="mt-3">
          Enfin, la formule{" "}
          <strong className="text-[#0f1d2b] font-semibold">Luxe</strong> offre une prise en charge
          complète de votre déménagement. Notre équipe s&apos;occupe de la préparation, de
          l&apos;emballage, de la protection, du transport et de l&apos;installation de vos biens
          afin de vous permettre de déménager avec un minimum de contraintes.
        </p>
        <p className="mt-3">
          <strong className="text-[#0f1d2b] font-semibold">Big Déménagement</strong> vous conseille
          la formule la mieux adaptée en fonction du volume à transporter, des accès, de la
          distance et de vos besoins. Consultez notre{" "}
          <Link
            to="/formules"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            page dédiée pour comparer toutes nos formules de déménagement
          </Link>{" "}
          et demander un devis personnalisé.
        </p>

        <div className="mt-6 flex flex-col sm:flex-row gap-2.5">
          <Link
            to="/formules"
            data-testid="home-faq-formules-cta"
            className="h-11 inline-flex items-center justify-center gap-2 rounded-xl px-5 bg-[#237cb5] hover:bg-[#1b5d8a] text-white text-[14px] font-semibold shadow-[0_18px_30px_-12px_rgba(35,124,181,0.45)] transition-colors whitespace-nowrap"
          >
            Comparer nos formules
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/demande-de-devis"
            data-testid="home-faq-formules-devis-cta"
            className="h-11 inline-flex items-center justify-center gap-2 rounded-xl px-5 bg-white border border-[#e6edf3] hover:border-[#237cb5] text-[#0f1d2b] hover:text-[#237cb5] text-[14px] font-semibold transition-colors whitespace-nowrap"
          >
            Demander un devis personnalisé
          </Link>
        </div>
      </>
    ),
  },
  {
    q: "Démontez-vous et remontez-vous les meubles ?",
    a: (
      <>
        <p>
          Oui, <strong className="text-[#0f1d2b] font-semibold">Big Déménagement</strong> peut
          prendre en charge le{" "}
          <Link
            to="/montage-demontage-meubles"
            data-testid="home-faq-demontage-link"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            démontage et le remontage
          </Link>{" "}
          de vos meubles lorsque cela est nécessaire. Cette prestation est particulièrement utile
          pour les{" "}
          <strong className="text-[#0f1d2b] font-semibold">
            armoires, lits, bibliothèques, tables
          </strong>{" "}
          ou meubles volumineux qui ne peuvent pas être transportés en une seule pièce.
        </p>
        <p className="mt-3">
          Nos déménageurs démontent les éléments avec soin, protègent chaque partie pendant le
          transport, puis remontent les meubles dans votre nouveau logement. Cela permet de{" "}
          <strong className="text-[#0f1d2b] font-semibold">limiter les risques de casse</strong>,
          de <strong className="text-[#0f1d2b] font-semibold">gagner du temps</strong> et de
          faciliter le passage dans les{" "}
          <strong className="text-[#0f1d2b] font-semibold">escaliers</strong>, les{" "}
          <strong className="text-[#0f1d2b] font-semibold">ascenseurs</strong> ou les accès
          étroits.
        </p>
        <p className="mt-3">
          Le démontage et le remontage des meubles dépendent de la{" "}
          <strong className="text-[#0f1d2b] font-semibold">formule choisie</strong> et des
          caractéristiques du mobilier. Lors de l&apos;établissement du devis, notre équipe vous
          indique précisément les prestations incluses et vous conseille la solution la plus
          adaptée à votre déménagement.
        </p>

        <div className="mt-6 flex flex-col sm:flex-row gap-2.5">
          <Link
            to="/formules"
            data-testid="home-faq-demontage-formules-cta"
            className="h-11 inline-flex items-center justify-center gap-2 rounded-xl px-5 bg-[#237cb5] hover:bg-[#1b5d8a] text-white text-[14px] font-semibold shadow-[0_18px_30px_-12px_rgba(35,124,181,0.45)] transition-colors whitespace-nowrap"
          >
            Voir les prestations par formule
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/demande-de-devis"
            data-testid="home-faq-demontage-devis-cta"
            className="h-11 inline-flex items-center justify-center gap-2 rounded-xl px-5 bg-white border border-[#e6edf3] hover:border-[#FF7A00] text-[#0f1d2b] hover:text-[#FF7A00] text-[14px] font-semibold transition-colors whitespace-nowrap"
          >
            Demander un devis personnalisé
          </Link>
        </div>
      </>
    ),
  },
  {
    q: "Combien de cartons prévoir pour un déménagement ?",
    a: (
      <>
        <p>
          Le dimensionnement du nombre de cartons nécessaires dépend de plusieurs facteurs
          clés : la <strong className="text-[#0f1d2b] font-semibold">superficie habitable</strong>,
          le nombre de pièces, et surtout le{" "}
          <Link
            to="/calculer-mon-volume"
            data-testid="home-faq-cartons-volume-link"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            cubage total de vos biens
          </Link>{" "}
          évalué lors de l&apos;étude préalable. À titre indicatif, voici les estimations moyennes
          observées pour calibrer votre stock de fournitures de déménagement :
        </p>
        <ul className="mt-4 space-y-2.5 pl-1">
          {[
            ["Studio / T1", "Environ 10 à 15 cartons (adapté pour un volume mobilier restreint et un inventaire de base)."],
            ["Appartement T2", "Environ 20 à 30 cartons (répartis entre le linge, les bibelots et les ustensiles de cuisine)."],
            ["Appartement T3", "Environ 30 à 50 cartons (incluant des conditionnements spécifiques pour la vaisselle et le vestiaire)."],
            ["Maison individuelle ou grand appartement (T4 et plus)", "Environ 50 à 80 cartons, voire davantage selon l'importance des volumes stockés dans les annexes (caves, garages, greniers)."],
          ].map(([t, d]) => (
            <li key={t} className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[#237cb5]" />
              <span>
                <strong className="text-[#0f1d2b] font-semibold">{t} :</strong> {d}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-5 font-extrabold text-[#0f1d2b]">
          Recommandations techniques pour un conditionnement optimal
        </p>
        <ul className="mt-3 space-y-2.5 pl-1">
          {[
            ["Adaptation des charges et des formats", "Privilégiez les petits cartons renforcés pour les ouvrages (livres), les charges denses et la vaisselle afin d'éviter toute rupture de fond sous le poids. Réservez les cartons grands formats exclusivement aux textiles, au linge de maison et aux objets légers."],
            ["Sécurisation des objets délicats", "Enveloppez minutieusement votre vaisselle, vos verreries et vos objets d'art dans du papier bulle multicouche, et utilisez des croisillons ou des calages professionnels pour neutraliser les contraintes cinématiques durant le transport routier."],
            ["Marge de sécurité logistique", "Prévoyez systématiquement une marge de 10 % à 15 % de cartons et de rouleaux d'adhésif industriels supplémentaires pour parer aux oublis de dernière minute et aux besoins d'emballage imprévus."],
          ].map(([t, d]) => (
            <li key={t} className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[#237cb5]" />
              <span>
                <strong className="text-[#0f1d2b] font-semibold">{t} :</strong> {d}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-5 font-extrabold text-[#0f1d2b]">
          L&apos;accompagnement expert de notre entreprise
        </p>
        <p className="mt-3">
          Notre équipe de déménageurs professionnels vous guide pas à pas dans le choix des kits
          de déménagement et des accessoires de protection adaptés à votre cubage exact. Nous
          mettons à votre disposition l&apos;ensemble du matériel industriel requis (cartons
          standards, cartons penderies, films bulles et adhésifs de signalisation) pour garantir
          l&apos;intégrité de vos biens du départ jusqu&apos;à l&apos;installation dans votre
          nouveau logement.
        </p>
      </>
    ),
  },
  {
    q: "Comment réduire le prix de son déménagement ?",
    a: (
      <>
        <p>
          Plusieurs solutions professionnelles permettent de réduire significativement le{" "}
          <Link
            to="/tarifs-demenagement-paris"
            data-testid="faq-link-tarifs"
            className="font-semibold text-[#237cb5] underline underline-offset-2 hover:text-[#155a87]"
          >
            coût de votre transfert
          </Link>{" "}
          tout en conservant une prestation de haute qualité :
        </p>
        <ul className="mt-4 space-y-2.5 pl-1">
          {[
            ["Le tri amont et l'optimisation du cubage", "Faites le tri méthodique dans vos affaires avant le déménagement afin de réduire drastiquement le volume global à transporter, le tarif étant directement indexé sur les mètres cubes engagés."],
            ["L'autonomie dans le conditionnement", "Préparez vous-même vos cartons en amont en optant pour une formule de déménagement plus économique, ce qui allège la facture liée à la main-d'œuvre spécialisée."],
            ["La sélection rigoureuse des formules", "Comparez objectivement les différentes prestations proposées pour ne retenir que les options et les niveaux de service réellement nécessaires à votre projet."],
            ["La flexibilité calendaire", "Si votre planning le permet, choisissez une date de déménagement modulable en évitant les périodes de forte tension logistique (fins de mois, week-ends estivaux et pics de mutation) pour bénéficier de tarifs plus avantageux."],
            ["L'anticipation des accès et de la voirie", "Facilitez l'accès aux logements de départ et d'arrivée en planifiant en amont les autorisations administratives d'occupation du domaine public et les arrêtés de stationnement."],
            ["La transparence tarifaire", "Exigez un devis de déménagement détaillé et exhaustif afin de cartographier précisément chaque poste budgétaire et de valider les garanties incluses."],
          ].map(([t, d]) => (
            <li key={t} className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[#237cb5]" />
              <span>
                <strong className="text-[#0f1d2b] font-semibold">{t} :</strong> {d}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-4">
          Le prix d&apos;un transfert dépend de critères techniques précis : le cubage total à
          déménager, la distance kilométrique, l&apos;accessibilité architecturale des logements,
          la saisonnalité de la date et les services choisis. Notre équipe d&apos;experts est à
          votre disposition pour analyser vos impératifs financiers et vous orienter vers la
          solution logistique la plus adaptée à votre budget, sans jamais compromettre la sécurité
          ni le soin apporté à vos meubles et effets personnels. Pour affiner votre budget,
          comparez nos{" "}
          <Link
            to="/formules"
            data-testid="home-faq-prix-formules-link"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            formules de déménagement
          </Link>{" "}
          et demandez votre{" "}
          <Link
            to="/demande-de-devis"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            devis gratuit
          </Link>
          .
        </p>
      </>
    ),
  },
  {
    q: "Que comprend une prestation de déménagement professionnel ?",
    a: (
      <>
        <p className="font-extrabold text-[#0f1d2b]">
          Une prestation de déménagement professionnel sur mesure
        </p>
        <p className="mt-3">
          Une prestation de déménagement professionnel s&apos;adapte rigoureusement au cubage de
          votre inventaire, à la typologie de votre logement et à vos exigences. Elle comprend un
          ensemble de services fondamentaux pour sécuriser votre transfert :
        </p>
        <ul className="mt-4 space-y-2.5 pl-1">
          {[
            ["Protection et conditionnement", "Capitonnage intégral de votre mobilier sous couvertures de protection et emballage minutieux des éléments pour contrer les contraintes cinématiques du transport routier."],
            ["Mise en carton des objets fragiles", "Prise en charge experte par nos déménageurs de votre vaisselle, de vos verreries et de vos objets d'art, conditionnés dans des emballages spécifiques et renforcés."],
            ["Manutention et transport", "Portage méthodique de vos charges, calage optimisé dans le camion et acheminement sécurisé via notre flotte de véhicules industriels capitonnés."],
            ["Démontage et remontage du mobilier", "Dépose et réassemblage technique de vos armoires, lits, buffets et structures par des techniciens qualifiés (selon la formule choisie)."],
            ["Prestations d'emballage global", "Mise en cartons de l'intégralité de vos biens personnels et déballage soigné lors des formules complètes « clés en main »."],
            ["Utilisation de matériel technique", "Mise en œuvre de tous les accessoires professionnels indispensables (sangles d'arrimage, chariots de roulage, cartons penderies, films bulles et housses de protection)."],
            ["Livraison au logement et installation", "Déchargement des biens et mise en place directe de votre mobilier et de vos cartons dans chaque pièce de votre nouveau foyer."],
          ].map(([t, d]) => (
            <li key={t} className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[#237cb5]" />
              <span>
                <strong className="text-[#0f1d2b] font-semibold">{t} :</strong> {d}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-5 font-extrabold text-[#0f1d2b]">
          Nos services complémentaires à la carte
        </p>
        <p className="mt-3">
          Pour répondre précisément à chaque configuration de chantier et surmonter toutes les
          contraintes logistiques, notre entreprise propose également des prestations
          additionnelles mobilisables selon vos besoins :
        </p>
        <ul className="mt-4 space-y-2.5 pl-1">
          {[
            ["Livraison de cartons à domicile", "Acheminement direct de vos kits d'emballage, de vos cartons renforcés et de vos adhésifs professionnels directement chez vous pour vous permettre de préparer votre transfert à votre rythme et en toute sérénité."],
            ["Monte-meuble", "Solution de levage aérien par les fenêtres ou les balcons pour hisser rapidement et en toute sécurité les volumes lourds ou encombrants lorsque les cages d'escalier ou les ascenseurs sont inadaptés."],
            ["Stockage en garde-meuble", "Espaces de stockage hautement sécurisés pour entreposer temporairement vos biens dans des conditions hygrométriques optimales lors de vos transitions immobilières."],
            ["Service débarras", "Intervention méthodique pour le tri, l'évacuation et le désencombrement de vos annexes (caves, greniers, garages, ateliers) ou de vos encombrants."],
            ["Réservation de stationnement", "Prise en charge intégrale des démarches administratives obligatoires auprès des services municipaux pour obtenir les arrêtés de voirie et garantir l'emplacement de nos camions au pied de votre immeuble."],
          ].map(([t, d]) => (
            <li key={t} className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[#237cb5]" />
              <span>
                <strong className="text-[#0f1d2b] font-semibold">{t} :</strong> {d}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-4">
          En savoir plus sur nos services complémentaires : notre{" "}
          <Link
            to="/monte-meuble"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            monte-meuble
          </Link>
          , notre{" "}
          <Link
            to="/garde-meuble"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            garde-meuble sécurisé
          </Link>
          , notre service{" "}
          <Link
            to="/debarras-vide-maison"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            débarras &amp; vide-maison
          </Link>{" "}
          et la{" "}
          <Link
            to="/autorisation-de-stationnement-demenagement"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            réservation de stationnement
          </Link>
          .
        </p>
        <p className="mt-4">
          Selon votre situation, notre entreprise propose différentes{" "}
          <Link to="/formules" className="text-[#237cb5] font-semibold underline hover:text-[#1a5f8f]">
            formules de déménagement modulables
          </Link>{" "}
          afin de vous offrir une prestation parfaitement ajustée à votre budget, à votre volume
          global et au niveau d&apos;accompagnement souhaité.
        </p>
      </>
    ),
  },
  {
    q: "Comment préparer efficacement son déménagement étape par étape ?",
    a: (
      <>
        <p>
          Pour aborder votre changement d&apos;adresse dans les meilleures conditions et ne rien
          oublier, voici le calendrier chronologique idéal à suivre :
        </p>
        <div className="mt-4 space-y-4">
          {[
            ["À J-30 : Le grand tri et les démarches initiales", "Faites le tri méthodique de vos affaires (pièce par pièce, y compris les caves et greniers) pour réduire le volume à transporter. Commandez vos cartons et kits d'emballage, et commencez à signaler votre changement d'adresse auprès des organismes clés (banques, assurances, abonnements, impôts, caisses de retraite et fournisseurs d'énergie). Si vous gérez vous-même la voirie, effectuez la demande d'autorisation de stationnement auprès de la mairie."],
            ["À J-15 : L'emballage et l'organisation pratique", "Commencez à emballer les objets non essentiels (livres, vêtements hors-saison, vaisselle de fête et bibelots). Étiquetez clairement chaque carton en y indiquant la pièce de destination et le contenu. Regroupez l'ensemble de vos documents administratifs importants (contrats, pièces d'identité, dossiers médicaux) dans une pochette dédiée que vous garderez précieusement avec vous."],
            ["À J-7 : Les derniers préparatifs et la logistique finale", "Démontez le mobilier non indispensable et regroupez la visserie dans des sachets étiquetés. Commencez à vider, nettoyer et dégivrer votre réfrigérateur ainsi que votre congélateur. Préparez une « valise de survie » ou un carton spécifique regroupant les affaires de première urgence pour les premiers jours (vêtements de rechange, trousse de toilette, chargeurs, papiers et quelques produits d'entretien)."],
            ["À J-2 : La dernière ligne droite", "Finalisez les derniers cartons de la vie quotidienne (vêtements de la semaine, ustensiles de cuisine indispensables). Vérifiez que tout est prêt pour le chargement et facilitez l'accès au logement en libérant les cheminements. Confirmez les derniers détails logistiques avec votre équipe de déménageurs."],
            ["Le jour J : Le grand départ", "Accueillez l'équipe de déménageurs, effectuez le tour du propriétaire pour valider l'état des lieux et l'inventaire, et assistez au chargement du camion. À l'arrivée dans votre nouveau logement, guidez les déménageurs pour le positionnement du mobilier et des cartons dans chaque pièce, puis vérifiez l'intégrité de vos biens avant de signer la lettre de voiture."],
            ["Après le déménagement : La réinstallation", "Procédez au déballage par ordre de priorité (commencez par la cuisine, la salle de bains et les chambres pour retrouver un confort immédiat). Montez progressivement le reste de votre mobilier et commencez le rangement des pièces à vivre. Enfin, actualisez votre adresse sur votre carte grise et finalisez vos transferts de lignes internet et téléphoniques."],
          ].map(([t, d]) => (
            <div key={t}>
              <p className="font-semibold text-[#0f1d2b]">{t}</p>
              <p className="mt-1.5">{d}</p>
            </div>
          ))}
        </div>
        <p className="mt-4">
          Bon à savoir : Big Déménagement peut gérer pour vous la{" "}
          <Link
            to="/autorisation-de-stationnement-demenagement"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            demande d&apos;autorisation de stationnement
          </Link>{" "}
          et vous conseiller sur le{" "}
          <Link
            to="/calculer-mon-volume"
            className="text-[#237cb5] underline underline-offset-2 hover:text-[#1b5d8a] font-semibold"
          >
            calcul de votre volume
          </Link>{" "}
          avant le jour J.
        </p>
      </>
    ),
  },
];

function HomeFaqItem({ item, index, open, onToggle }) {
  const itemRef = useRef(null);
  const handleClick = () => {
    const el = itemRef.current;
    const before = el ? el.getBoundingClientRect().top : 0;
    onToggle();
    requestAnimationFrame(() => {
      if (!el) return;
      const after = el.getBoundingClientRect().top;
      const delta = after - before;
      if (delta !== 0) window.scrollBy({ top: delta, behavior: "instant" });
    });
  };
  return (
    <li
      ref={itemRef}
      className="rounded-2xl bg-[#f7fafc] border border-[#e6edf3] shadow-[0_10px_24px_-18px_rgba(35,124,181,0.35)]"
    >
      <button
        type="button"
        data-testid={`home-faq-q-${index}`}
        onClick={handleClick}
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-4 text-left px-5 sm:px-6 py-3 sm:py-3.5"
      >
        <span className="text-[14.5px] sm:text-[15px] font-extrabold text-[#0f1d2b]">{item.q}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-[#237cb5] transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div
          data-testid={`home-faq-a-${index}`}
          className="px-5 sm:px-6 pb-5 sm:pb-6 text-[14px] sm:text-[15px] text-[#3a4a5a] leading-relaxed"
        >
          {item.a}
        </div>
      )}
    </li>
  );
}

function HomeFaqList({ items }) {
  const [openIndex, setOpenIndex] = useState(-1);
  return (
    <ul className="mt-10 max-w-4xl space-y-3">
      {items.map((item, i) => (
        <HomeFaqItem
          key={item.q}
          item={item}
          index={i}
          open={openIndex === i}
          onToggle={() => setOpenIndex((prev) => (prev === i ? -1 : i))}
        />
      ))}
    </ul>
  );
}

export default function Home() {
  useSEO({
    title: "Déménageur Île-de-France : Devis dès 600 € (20 m³) | Big Déménagement",
    description:
      "Big Déménagement accompagne particuliers et entreprises pour leur déménagement à Paris et en Île-de-France. Devis gratuit, formules adaptées, monte-meuble, garde-meuble et service professionnel.",
    canonical: "https://bigdemenagement.fr/",
    jsonLd: HOME_JSONLD,
  });

  return (
    <div data-testid="home-page">
      <Hero />
      <Avis />
      <Services />
      <PrixDemenagement />
      <Galerie />
      <Formules />

      <ZonesInterventions />
      <PourquoiChoisir />

      {/* CTA — Besoin d'un déménagement particulier ? */}
      <section
        data-testid="home-cta-particulier"
        className="py-10 sm:py-14 bg-white"
      >
        <div className="bd-container">
          <div className="relative overflow-hidden rounded-3xl bg-[#237cb5] text-white p-8 sm:p-12 shadow-[0_40px_80px_-40px_rgba(35,124,181,0.55)]">
            <div
              className="absolute -top-24 -right-16 h-72 w-72 rounded-full blur-3xl opacity-30"
              style={{ background: "#5793d8" }}
              aria-hidden="true"
            />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              <div className="lg:col-span-8">
                <h2 className="text-[21px] sm:text-3xl lg:text-[34px] font-extrabold leading-tight">
                  Besoin d&apos;un déménagement particulier en Île-de-France ?
                </h2>
                <p className="mt-3 text-white/85 text-[15px] sm:text-base leading-relaxed max-w-2xl">
                  Obtenez rapidement un devis gratuit et personnalisé selon votre logement, votre
                  date et vos besoins.
                </p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <Link
                  to="/demande-de-devis"
                  data-testid="home-cta-particulier-devis"
                  className="h-12 inline-flex items-center justify-center gap-2 rounded-xl px-6 bg-[#FF7A00] hover:bg-[#E66E00] text-white text-[14px] font-semibold shadow-[0_18px_30px_-12px_rgba(255,122,0,0.55)] transition-colors whitespace-nowrap"
                >
                  Devis gratuit
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>


      <AssuranceSection />

      {/* Question / Réponse — bas de page d'accueil (SEO + UX) */}
      <section
        id="qr"
        data-testid="home-qr"
        className="py-10 sm:py-14 bg-white"
        aria-labelledby="home-qr-title"
      >
        <div className="bd-container">
          <div className="max-w-3xl">
            <span className="bd-eyebrow">Q & R</span>
            <h2
              id="home-qr-title"
              className="bd-h2 mt-4 text-[26px] sm:text-[30px] lg:text-[36px]"
            >
              Questions fréquentes sur le déménagement en{" "}
              <span style={{ color: "#237cb5" }}>Île-de-France</span>
            </h2>
            <p className="mt-4 text-[#3a4a5a] leading-relaxed text-[15px] sm:text-base">
              Tout ce qu&apos;il faut savoir pour obtenir une estimation claire et comprendre ce
              qui fait varier le prix de votre déménagement en Île-de-France.
            </p>
          </div>

          <HomeFaqList items={HOME_FAQ_VISIBLE} />
        </div>
      </section>

      <NotreAdresse />
    </div>
  );
}
